import { Component, inject, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { RuleInputDialogComponent } from 'libs/shared/ui-common/src/lib/components/rule-input/rule-input-dialog/rule-input-dialog.component';
import { MedicalComponentSearchComponent, FilterOptions, SearchResultDto } from 'shared/feature-medical-component-search';
import { DialogModule, DialogRef, DialogService, TooltipDirective } from 'shared/ui-common';

import {
  ComponentState,
  ComponentType,
  DIALOG_DATA,
  FlowchartDecisionNodeDto,
  FlowchartElementType,
  FlowchartStartNodeDto,
  ProposedComponentDto,
} from '@treetop/shared/model';

@Component({
  selector: 'app-medical-component-search-dialog',
  templateUrl: './medical-component-search-dialog.component.html',
  styleUrls: ['./medical-component-search-dialog.component.scss'],
  standalone: true,
  imports: [
    MatButtonModule,
    TranslocoModule,
    MedicalComponentSearchComponent,
    MatFormFieldModule,
    MatIconModule,
    TooltipDirective,
    DialogModule,
  ],
})
export class MedicalComponentSearchDialogComponent implements OnInit {
  public data: any = inject(DIALOG_DATA) as any;
  private dialogRef: DialogRef = inject(DialogRef);
  private dialogService = inject(DialogService);
  private router = inject(Router);

  sidebarElement;
  definition;
  possibleComponentTypes = [];

  flowchartElementType = FlowchartElementType;

  searchOptions: FilterOptions = {
    possibleComponentTypes: [],
    taxonomyId: this.data.taxonomyId,
    useProposedComponent: true,
  };

  ngOnInit() {
    const possibleComponentTypes = [];
    if (this.data.activityFilterActive) {
      possibleComponentTypes.push(ComponentType.ActivityDefinition);
    }
    if (this.data.findingFilterActive) {
      possibleComponentTypes.push(ComponentType.FindingDefinition);
    }
    if (this.data.observationFilterActive) {
      possibleComponentTypes.push(ComponentType.ObservationDefinition);
    }
    this.searchOptions.possibleComponentTypes = possibleComponentTypes;
    if (this.data.isFilterSearch) {
      this.searchOptions.usableComponents = false;
      possibleComponentTypes.push(ComponentType.ActivityDefinition);
      this.searchOptions.possibleComponentSubTypes = { [ComponentType.ActivityDefinition]: 9 } as { [key in ComponentType]: number };
      this.searchOptions.componentStates = [ComponentState.Published];
    } else {
      this.searchOptions.usableComponents = true;
    }
    if (
      this.data.elementType === FlowchartElementType.FlowchartContainer ||
      this.data.elementType === FlowchartElementType.FlowchartSubflow
    ) {
      this.searchOptions.useProposedComponent = false;
    }
  }

  closeDialogWithSelection(component: FormControl) {
    this.dialogRef.close(component);
  }

  proposeComponent(component: string | ProposedComponentDto) {
    this.dialogRef.close(component);
  }

  closeDialog() {
    this.dialogRef.close(null);
  }

  selectedComponent(searchResultDto: SearchResultDto) {
    if (searchResultDto.componentDto) {
      this.dialogRef.close(new FormControl(searchResultDto.componentDto));
    } else if (searchResultDto.proposedComponentDto) {
      this.dialogRef.close(new FormControl(searchResultDto.proposedComponentDto));
    } else if (searchResultDto.newComponentText) {
      this.dialogRef.close(searchResultDto.newComponentText);
    }
  }

  protected openRuleDialog($event) {
    if (this.data.sidebarElement) {
      $event.stopPropagation();
      let rule = '';
      let prettyfiedRule = '';
      if (this.data.sidebarElement.elementType === FlowchartElementType.FlowchartDecisionNode) {
        const flowchartDecisionNode = this.data.sidebarElement as FlowchartDecisionNodeDto;
        rule = flowchartDecisionNode.rule;
        prettyfiedRule = flowchartDecisionNode.rule;
      } else if (this.data.sidebarElement.elementType === FlowchartElementType.FlowchartStartNode) {
        const flowchartStartNode = this.data.sidebarElement as FlowchartStartNodeDto;
        rule = flowchartStartNode.rule;
        prettyfiedRule = flowchartStartNode.rule;
      }
      const ruleDialog = this.dialogService.open(RuleInputDialogComponent, {
        width: '60%',
        title: 'Definition',
        data: {
          rule,
          prettyfiedRule,
          publishedComponents: true,
        },
      });

      ruleDialog.afterClosed().subscribe(({ rule, prettified }) => {
        if (rule && prettified) {
          if (this.data.sidebarElement.elementType === FlowchartElementType.FlowchartDecisionNode) {
            const ruleTitle = new DOMParser().parseFromString(prettified, 'text/html').documentElement.textContent;
            const flowchartDecisionNode = this.data.sidebarElement as FlowchartDecisionNodeDto;
            flowchartDecisionNode.rule = rule;
            flowchartDecisionNode.rulePrettyfied = prettified;
            flowchartDecisionNode.ruleTitle = ruleTitle;
            flowchartDecisionNode.guidelineCriteria = null;
            flowchartDecisionNode.guidelineCriteriaId = null;
            flowchartDecisionNode.guidelineObservation = null;
            flowchartDecisionNode.guidelineObservationId = null;
          } else if (this.data.sidebarElement.elementType === FlowchartElementType.FlowchartStartNode) {
            const flowchartStartNode = this.data.sidebarElement as FlowchartStartNodeDto;
            flowchartStartNode.rule = rule;
            flowchartStartNode.rulePrettyfied = prettified;
          }
          const formControl = new FormControl('');
          formControl.patchValue(this.data.sidebarElement as string);
          this.dialogRef.close(formControl);
        }
      });
    }
  }
}
